import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { Box } from '@mui/material'
import QuotePDF from './QuotePDF'
import { useLocation } from 'react-router-dom'
import Quote from './Quote'
import { getObject, getObjectsWithFilter, addObject, editObjectFilter } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'
import { useAuth } from '../../../context/AuthContext'
import Toolbar from '../../toolbar/Toolbar'
import { useOutletContext } from 'react-router-dom'
import SettingsIcon from '@mui/icons-material/Settings'
import EmailIcon from '@mui/icons-material/Email'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import PDFReader from '../../pdfReader/PDFReader'
import useNavigate from '../../hooks/useNavigate'
import TransformIcon from '@mui/icons-material/Transform'
import {
  copyQuote,
  createEntity,
  createInterventionFromQuote,
  createInvoiceFromQuote,
  fileToBase64,
  updateAttachments,
  updateDocumentStatus
} from '../../../utils/documentManager'
import StatusField from '../../fields/StatusField'
import ActionMenu from '../../common/ActionMenu'
import Loading from '../../common/Loading'
import { sendDocumentEmail } from '../../../utils/emailUtils'
import StatusDialog from '../../dialogs/StatusDialog'
import NotFound from '../../common/NotFound'
import DocumentPreview from '../../document/DocumentPreview'
import { pdf } from '@react-pdf/renderer'
import { ContentCopy } from '@mui/icons-material'

const ViewQuote = () => {
  const [quote, setQuote] = useState({})
  const [company, setCompany] = useState([])
  const [img, setImg] = useState('')
  const [showPdf, setShowPdf] = useState(null)
  const [statusList, setStatusList] = useState([])
  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false)
  const [isStatusChanged, setIsStatusChanged] = useState(false)
  const [files, setFiles] = useState([])
  const [pdfUrl, setPdfUrl] = useState('')
  const [settings, setSettings] = useState(null)
  const history = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const [loading, setLoading] = useState(true)
  const toastId = useRef(null)

  const transformed = useCallback(() => {
    setQuote((prevQuote) => ({ ...prevQuote, status: 90 }))
    setIsStatusChanged(true)
  }, [])

  const openMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const closeMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const transformToInvoice = useCallback(() => {
    createEntity({
      toast,
      toastIdRef: toastId,
      history,
      user,
      entityType: 'invoices',
      settingsKey: 'invoice_number',
      factoryFn: createInvoiceFromQuote,
      args: [quote],
      redirectPath: '/invoices/edit',
      successMessage: 'La facture a été créée avec succès',
      updateHistoryState: { history: '/invoices/view' },
      updateCounter: true,
      counterName: 'nb_invoice',
      contractLineName: 'invoices',
      onSuccess: transformed
    })
  }, [toast, history, user, quote, transformed])

  const transformToIntervention = useCallback(() => {
    createEntity({
      toast,
      toastIdRef: toastId,
      history,
      user,
      entityType: 'interventions',
      settingsKey: 'intervention_number',
      factoryFn: createInterventionFromQuote,
      args: [quote],
      redirectPath: '/interventions/edit',
      successMessage: "L'intervention a été créée avec succès",
      updateHistoryState: { history: '/interventions/view' },
      updateCounter: true,
      counterName: 'nb_intervention',
      contractLineName: 'interventions',
      onSuccess: transformed
    })
  }, [toast, history, user, quote, transformed])

  const sendEmail = useCallback(() => {
    try {
      sendDocumentEmail({
        type: 'devis',
        object: quote,
        objectType: 'quote',
        user,
        company,
        customer: quote.customer,
        template: company.quote_email_template,
        component: <QuotePDF quote_param={quote} company_param={company} logo_param={img} settings={settings} />,
        setFiles,
        subjectPrefix: 'Votre devis',
        statusToUpdate: 70,
        updateStatus: (newStatus) => {
          setQuote((prev) => ({ ...prev, status: newStatus }))
          setIsStatusChanged(true)
        }
      })
    } catch (error) {
      toast.error('Erreur lors de l’envoi de l’email')
    }
  }, [quote, user, company, img, settings])

  const openStatusDialog = useCallback(() => {
    setIsStatusDialogOpen(true)
  }, [])

  const closeStatusDialog = useCallback(
    (newValue) => {
      setIsStatusDialogOpen(false)
      if (newValue !== undefined && newValue !== quote.status) {
        setQuote((prevQuote) => ({ ...prevQuote, status: newValue }))
        setIsStatusChanged(true)
      }
    },
    [quote.status]
  )

  const menu = useMemo(
    () => [
      { label: 'Intervention', onClick: transformToIntervention },
      { label: 'Facture', onClick: transformToInvoice }
    ],
    [transformToInvoice, transformToIntervention]
  )

  useEffect(() => {
    if (!isStatusChanged) return
    updateDocumentStatus('quotes', quote, user, toast, setIsStatusChanged)
  }, [isStatusChanged, quote, user])

  useEffect(() => {
    setQuote((prev) => ({
      ...prev,
      attachments: updateAttachments(prev.attachments, files)
    }))
  }, [files])

  useEffect(() => {
    const quoteId = location?.state?.id
    if (!quoteId) {
      toast.error('Devis introuvable.', { autoClose: 2000 })
      return
    }

    const fetchAll = async () => {
      try {
        const [statusRes, quoteRes, companyRes, settingsRes] = await Promise.all([
          getObjectsWithFilter('status', { object: 'quotes' }),
          getObject('quotes', quoteId),
          getObject('company', user.company_id),
          getObjectsWithFilter('settings', { company_id: user.company_id })
        ])

        setStatusList(statusRes.data)
        setQuote(quoteRes.data)
        setCompany(companyRes.data)
        setSettings(settingsRes.data)

        if (quoteRes.data.attachments) {
          setFiles(quoteRes.data.attachments)
          const attach = quoteRes.data.attachments.find((f) => f.type === 'quote')
          if (attach) {
            const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
            const base64 = await fileToBase64(blob)
            setPdfUrl(`data:application/pdf;base64,${base64}`)
          }
        }

        const logoAttach = companyRes.data.attachments?.find((a) => a.type === 'logo')
        if (logoAttach) {
          const blob = new Blob([new Uint8Array(logoAttach.file.data)])
          const base64 = await fileToBase64(blob)
          setImg(base64)
        }
      } catch (error) {
        toast.error('Erreur lors du chargement des données', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    fetchAll()
  }, [location, user.company_id])

  useEffect(() => {
    if (!quote?.id) return

    let url
    const generatePdfFromComponent = async () => {
      try {
        const attach = quote.attachments?.find((f) => f.type === 'quote')
        if (attach && attach.file?.data?.length) {
          const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        } else {
          const component = (
            <QuotePDF quote_param={quote} company_param={company} logo_param={img} settings={settings} />
          )
          const doc = pdf(component)
          const blob = await doc.toBlob()
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        }
      } catch (err) {}
    }

    generatePdfFromComponent()
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [quote, company, img, settings])

  const toolbarOptions = useMemo(
    () => ({
      objectType: 'quotes',
      title: () => {
        return `Devis ${quote.quote_number}`
      },
      accessRight: userRight,
      actions: {
        edit: true,
        editDisabled: () => quote.status >= 90,
        files: true,
        back: true,
        audits: true,
        list: true
      },
      customActionsRight: [
        {
          icon: () => <PictureAsPdfOutlinedIcon />,
          tooltip: 'Afficher PDF',
          onClick: () => setShowPdf((prev) => !prev)
        },
        {
          icon: () => <EmailIcon />,
          tooltip: 'Envoyer par mail',
          onClick: sendEmail,
          disabled: () => !quote.customer?.email
        },
        {
          icon: () => <SettingsIcon />,
          tooltip: 'Changer statut',
          onClick: openStatusDialog,
          disabled: () => quote.status >= 90
        },
        {
          icon: () => <ContentCopy />,
          tooltip: 'Dupliquer',
          onClick: () => duplicate()
        },
        {
          icon: () => <TransformIcon />,
          tooltip: 'Transformer',
          disabled: () => quote.status >= 90 || quote.status < 80,
          onClick: openMenu
        }
      ],
      customComponentsLeft: [
        {
          component: () => <StatusField value={quote.status} type='quotes' statusList={statusList} />
        }
      ]
    }),
    [userRight, quote, statusList, sendEmail, openStatusDialog, openMenu]
  )

  const duplicate = useCallback(() => {
    createEntity({
      toast,
      toastIdRef: toastId,
      getObjectsWithFilter,
      addObject,
      editObjectFilter,
      history,
      user,
      entityType: 'quotes',
      settingsKey: 'quote_number',
      factoryFn: copyQuote,
      args: [quote],
      redirectPath: '/quotes/edit',
      successMessage: `Le devis a été dupliquée avec succès`,
      updateHistoryState: { history: '/quotes/view' }
    })
  }, [toast, history, user, quote])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!quote?.id) return <NotFound title='Devis introuvable' />

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar
        options={toolbarOptions}
        object={quote}
        setObject={setFiles}
        setParentObject={setQuote}
        parentObject={quote}
      />
      {showPdf ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <PDFReader url={pdfUrl} filename={`devis_${quote?.quote_number}.pdf`} />
        </Box>
      ) : (
        <Box sx={{ overflowY: 'auto', flexGrow: 1, paddingTop: '15px', paddingBottom: '15px' }}>
          <DocumentPreview Component={Quote} document={quote} />
        </Box>
      )}

      <StatusDialog
        statusList={statusList}
        open={isStatusDialogOpen}
        onClose={closeStatusDialog}
        type='quotes'
        value={quote.status}
      />
      <ActionMenu anchorEl={anchorEl} onClose={closeMenu} menuItems={menu} />
    </Box>
  )
}

export default ViewQuote
